// Conversational SEO — client-side execution of the agent's WordPress READ tools.
// These use the browser's live WPClient (same auth the rest of the app uses). Read-only in P2,
// so they run automatically without a confirmation step. Each returns an LLM-facing string.

import type { WPClient } from "./wpClient";
import { extractElementorBlocks, elementorPlainText, extractElementorLinks } from "./elementorContent";

function cap(v: unknown, max = 12000): string {
  const s = typeof v === "string" ? v : JSON.stringify(v);
  return s.length > max ? s.slice(0, max) + `\n…[truncated ${s.length - max} chars]` : s;
}

// Does this URL point at the site root (the homepage)? A custom/static front page's
// permalink is the root, which usually won't string-match the slug URL a caller passes,
// so when the target is the root we resolve it via the front-page setting instead.
function isRootUrl(u: string): boolean {
  const s = u.trim();
  if (s === "" || s === "/") return true;
  try { return new URL(s).pathname.replace(/\/+$/, "") === ""; } catch { return false; }
}

export async function dispatchWpRead(
  name: string,
  input: Record<string, unknown>,
  client: WPClient,
): Promise<{ ok: boolean; content: string }> {
  try {
    switch (name) {
      case "wp_site_info": {
        const [plugin, editor, title] = await Promise.all([
          client.detectPlugin(),
          client.detectEditor(),
          client.getSiteTitle().catch(() => ""),
        ]);
        return { ok: true, content: cap({ seoPlugin: plugin, editor, siteTitle: title }) };
      }
      case "wp_list_pages": {
        const all = await client.getAllPagesAndPosts();
        const type = input.type;
        const filtered = (type === "page" || type === "post") ? all.filter(p => p.type === type) : all;
        const rows = filtered.slice(0, 400).map(p => ({ type: p.type, id: p.id, url: p.url, title: p.title, slug: p.slug, parent: p.parent, tags: p.tags }));
        return { ok: true, content: cap({ count: filtered.length, shown: rows.length, pages: rows }) };
      }
      case "wp_get_page": {
        let id = typeof input.id === "number" ? input.id : null;
        let type: "pages" | "posts" = input.type === "posts" ? "posts" : "pages";
        const url = typeof input.url === "string" ? input.url.trim() : "";
        if (!id && url) {
          // Homepage-aware resolution. A static/custom front page's permalink is the site
          // root, so the slug URL a caller passes rarely string-matches it — try the
          // authoritative front-page setting first (admin-only; harmless if forbidden),
          // then fall back to exact URL matching, which works for non-admins too.
          if (isRootUrl(url)) {
            const fp = await client.getFrontPageId().catch(() => null);
            if (fp) { id = fp; type = "pages"; }
          }
          if (!id) id = await client.getPageIdByUrl(url);
        }
        if (!id) return { ok: false, content: "Error: couldn't resolve the page — provide a valid 'url', or 'id' plus 'type'. (If this is the homepage and the front page is the blog index, there is no editable Page at the root URL.)" };
        // Detect the builder in one call. Elementor stores content as a JSON tree in postmeta,
        // not post_content, so we read it via the LSG plugin's builder endpoint and return the
        // page's real text + the addressable editable blocks + links (no truncation noise). Edit
        // Elementor pages with wp_edit_elementor using these block ids/fields.
        const model = await client.getBuilderModel(type, id).catch(() => null);
        if (model?.builder === "elementor" && Array.isArray(model.elements) && model.elements.length) {
          const blocks = extractElementorBlocks(model.elements);
          const text = elementorPlainText(model.elements);
          const links = extractElementorLinks(model.elements).slice(0, 100);
          return { ok: true, content: cap({ id, type, builder: "elementor", text, editableBlocks: blocks, links, editHint: "Edit this page with wp_edit_elementor — target a block by its id (and field), or use findReplace." }) };
        }
        // Non-Elementor: prefer the raw (editable) body; fall back to rendered HTML when it's
        // empty (e.g. a builder that keeps content outside post_content but renders via the_content).
        let content = model?.content ?? await client.getRawContent(type, id);
        let source: "raw" | "rendered" = "raw";
        if (!content || !content.trim()) {
          const rendered = await client.getRenderedContent(type, id).catch(() => null);
          if (rendered && rendered.trim()) { content = rendered; source = "rendered"; }
        }
        if (content == null || !content.trim()) {
          return { ok: false, content: `Error: no readable content for ${type} id ${id}. It may be built with a page builder that stores its content outside post_content (e.g. Elementor's _elementor_data). Ensure the Local SEO Garage plugin is active so builder content can be read.` };
        }
        return { ok: true, content: cap({ id, type, builder: model?.builder ?? "classic", source, content }) };
      }
      case "wp_get_audit_schema": {
        let id = typeof input.id === "number" ? input.id : null;
        let type: "pages" | "posts" = input.type === "posts" || input.type === "post" ? "posts" : "pages";
        const url = typeof input.url === "string" ? input.url.trim() : "";
        if (!id && url) {
          if (isRootUrl(url)) { const fp = await client.getFrontPageId().catch(() => null); if (fp) { id = fp; type = "pages"; } }
          if (!id) id = await client.getPageIdByUrl(url);
        }
        if (!id) return { ok: false, content: "Error: couldn't resolve the page — provide 'id' (and 'type'), or a valid 'url'." };
        const blocks = await client.getAuditSchema(id, type);
        return {
          ok: true,
          content: cap({
            id, type, count: blocks.length, blocks,
            note: blocks.length
              ? "These are the LSG-managed JSON-LD blocks currently on this page. To change them, send the FULL updated set to wp_set_schema (it replaces this set)."
              : "No LSG-managed schema on this page yet.",
          }),
        };
      }
    }
    return { ok: false, content: `Unknown WordPress tool "${name}".` };
  } catch (e) {
    return { ok: false, content: `Error: ${e instanceof Error ? e.message : "WordPress request failed"}` };
  }
}
